import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import {
  Accordion,
  Button,
  Col,
  Container,
  Form,
  Offcanvas,
  Row,
} from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  NavLink,
  useParams,
  useSearchParams,
  ScrollRestoration,
} from "react-router-dom";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import CategoryCard from "../components/CategoryCard";
import DataTable from "../components/DataTable";
import Meta from "../components/Meta";
import Callback from "../components/modals/Callback";
import ProductCard from "../components/ProductCard";
import ProjectItem from "../components/ProjectItem";
import ServiceItem from "../components/ServiceItem";
import Filter from "../components/svgs/Filter";
import PrevIcon from "../components/svgs/PrevIcon";
import Loader from "../components/utils/Loader";
import MultyRangeCustom from "../components/utils/MultyRangeCustom";
import NavTop from "../components/utils/NavTop";
import Select from "../components/utils/Select";
import SwiperButtonNext from "../components/utils/SwiperButtonNext";
import SwiperButtonPrev from "../components/utils/SwiperButtonPrev";
import { childrenArray, getImageURL } from "../helpers/all";
import { getCategory } from "../services/category";
import { removeFilter, updateFilter } from "../store/reducers/settingsSlice";

const Category = () => {
  const { t } = useTranslation();

  const options = useSelector((state) => state.settings.options);
  const selectedAffiliate = useSelector((state) => state.affiliate.active);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFeedback, setShowFeedback] = useState(false);
  const { categoryId } = useParams();
  const filters = useSelector(
    (state) =>
      state?.settings?.filter?.length > 0 &&
      state.settings.filter.find(
        (e) => Number(e.categoryId) === Number(categoryId)
      )
  );

  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [category, setCategory] = useState({
    loading: true,
    item: {},
  });

  const { control, setValue, reset } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: filters ?? { options: [] },
  });

  const data = useWatch({ control });

  const onLoad = useCallback(() => {
    setCategory((prev) => ({ ...prev, loading: true }));
    getCategory({
      ...data,
      id: categoryId,
      affiliateId: selectedAffiliate.id,
      page: searchParams.get("page"),
    })
      .then((res) => {
        res.params = childrenArray(res.params, "id", "parentId");
        setCategory({ loading: false, item: res });
      })
      .catch(() => setCategory((data) => ({ ...data, loading: false })));
  }, [categoryId, data, searchParams, selectedAffiliate?.id]);

  useLayoutEffect(() => {
    onLoad();
  }, [categoryId, data?.sort, selectedAffiliate?.id, searchParams.get("page")]);

  useEffect(() => {
    dispatch(updateFilter({ ...data, categoryId }));
  }, [data, categoryId]);

  const onFilter = useCallback(
    (option) => {
      let isOption =
        data?.options && data?.options?.length > 0
          ? option?.type == "select"
            ? data.options.findIndex(
                (e) =>
                  e.type == option.type && Number(e.id) == Number(option.id)
              )
            : data.options.findIndex(
                (e) =>
                  e.type == option.type &&
                  Number(e.value) == Number(option.value) &&
                  Number(e.id) == Number(option.id)
              )
          : -1;

      return isOption == -1 ? false : data?.options[isOption]?.value;
    },
    [data?.options]
  );

  const onChangeFilter = useCallback(
    (option) => {
      if (option) {
        if (data?.options?.length > 0) {
          if (option?.type == "checkbox") {
            let isOption = data.options.findIndex(
              (e) =>
                e.type == option.type &&
                Number(e.value) == Number(option.value) &&
                Number(e.id) == Number(option.id)
            );
            if (isOption >= 0 && isOption != -1) {
              setValue(
                "options",
                data.options.filter((e) => e.value != option.value)
              );
            } else {
              setValue("options", [...data.options, option]);
            }
          } else if (option?.type == "select") {
            let isOption = data.options.findIndex(
              (e) => e.type == option.type && Number(e.id) == Number(option.id)
            );

            if (
              option?.id &&
              (!option?.value || option.value == null || option.value == "NaN")
            ) {
              setValue(
                "options",
                data.options.filter((e) => e.value == null)
              );
            } else {
              if (isOption >= 0 && isOption != -1) {
                let options = [...data.options];
                options[isOption] = option;
                setValue("options", options);
              } else {
                setValue("options", [...data.options, option]);
              }
            }
          }
        } else {
          setValue("options", [option]);
        }
      }
    },
    [data?.options]
  );

  if (category?.loading) {
    return <Loader full />;
  }

  return (
    <main>
      <ScrollRestoration />
      <Meta
        title={
          options?.seo?.category?.title && category?.item?.title
            ? generateSeoText({
                text: options.seo.category.title,
                name: category.item.title,
                site: options?.title,
              })
            : selectedAffiliate?.title && category?.item?.title
            ? selectedAffiliate?.title + " - " + category.item.title
            : options?.title && category?.item?.title
            ? options.title + " - " + category.item.title
            : category?.item?.title ?? t("Категория")
        }
        description={
          options?.seo?.category?.description
            ? generateSeoText({
                text: options.seo.category.description,
                name: category.item.title,
                site: options?.title,
              })
            : category.item?.description ??
              t(
                "Добавьте блюдо из этой категории в корзину и наслаждайтесь вкусной едой прямо сейчас!"
              )
        }
        image={
          category?.item?.media
            ? getImageURL({
                path: category.item.media,
                size: "full",
                type: "category",
              })
            : false
        }
      />
      <section className="category mb-5">
        <Container>
          {category.item?.categories?.length > 1 && (
            <>
              <NavTop toBack={false} breadcrumbs={true} />
              <Swiper
                className="category-topSlider mb-5"
                spaceBetween={10}
                slidesPerView={2}
                speed={750}
                breakpoints={{
                  576: {
                    spaceBetween: 16,
                    slidesPerView: 3,
                  },
                  768: {
                    slidesPerView: 4,
                    spaceBetween: 16,
                  },
                  992: {
                    slidesPerView: 5,
                    spaceBetween: 16,
                  },
                  1200: {
                    slidesPerView: 6,
                    spaceBetween: 16,
                  },
                }}
              >
                {category.item.categories.map((obj) => {
                  return (
                    <SwiperSlide key={obj.id}>
                      <CategoryCard data={obj} />
                    </SwiperSlide>
                  );
                })}
                <SwiperButtonPrev />
                <SwiperButtonNext />
              </Swiper>
            </>
          )}

          <h1 className="mb-4 mb-lg-5">{category.item.title ?? "Категория"}</h1>
          <Row className="gx-5 mb-5">
            <Col lg={3} className="position-relative">
              <div className="position-sticky top-h">
                <Offcanvas
                  show={show}
                  onHide={handleClose}
                  className="offcanvas-filter"
                  responsive="lg"
                  placement="bottom"
                >
                  <Offcanvas.Body>
                    <form className="filter">
                      <div className="filter-heading">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="d-block d-lg-none fs-15 me-2"
                        >
                          <PrevIcon className="svgSW" />
                        </button>
                        <h5 className="mb-0">Фильтры</h5>
                        <button
                          type="reset"
                          className="ms-auto"
                          onClick={() => {
                            dispatch(removeFilter());
                            reset({
                              min: category.item?.min ?? 0,
                              max: category.item?.max ?? 100,
                            });
                          }}
                        >
                          очистить
                        </button>
                      </div>
                      {category.item?.min > 0 && category.item?.max > 0 && (
                        <fieldset>
                          <legend>Цена, ₽</legend>
                          <MultyRangeCustom
                            minRange={category.item?.min ?? 0}
                            maxRange={category.item?.max ?? 100}
                            valueMin={data?.min ?? category.item?.min ?? 0}
                            valueMax={data?.max ?? category.item?.max ?? 100}
                            onChange={(e) => e && reset({ ...data, ...e })}
                          />
                        </fieldset>
                      )}
                      <Accordion defaultActiveKey={0}>
                        {category.item?.params?.length > 0 &&
                          category.item?.params.map((e, index) => {
                            return e.type === "select" &&
                              e?.children?.length > 0 ? (
                              <fieldset>
                                {/* <legend>{e.title}</legend> */}
                                <Select
                                  label={t(e.title)}
                                  className="w-100 mb-2"
                                  onClick={(s) =>
                                    onChangeFilter({
                                      type: e.type,
                                      id: Number(e.id),
                                      value: s.value,
                                    })
                                  }
                                  value={onFilter({
                                    type: e.type,
                                    id: e.id,
                                  })}
                                  data={e.children.map((e) => ({
                                    title:
                                      e?.value?.length > 0 ? e.value : null,
                                    value: e,
                                  }))}
                                />
                              </fieldset>
                            ) : e.type === "checkbox" &&
                              e?.children?.length > 0 ? (
                              <Accordion.Item as="fieldset" eventKey={index}>
                                <Accordion.Header as="legend">
                                  {e.title}
                                </Accordion.Header>
                                <Accordion.Body>
                                  <ul>
                                    {e.children.map((item) => (
                                      <li>
                                        <Form.Check className="mb-2">
                                          <Form.Check.Input
                                            type="checkbox"
                                            name="checkbox"
                                            id={item.id}
                                            defaultChecked={onFilter({
                                              type: e.type,
                                              id: e.id,
                                              value: item.id,
                                            })}
                                            onChange={() =>
                                              onChangeFilter({
                                                type: e.type,
                                                id: Number(e.id),
                                                value: Number(item.id),
                                              })
                                            }
                                          />
                                          <Form.Check.Label
                                            htmlFor={item.id}
                                            className="ms-2"
                                          >
                                            {item.value}
                                          </Form.Check.Label>
                                        </Form.Check>
                                      </li>
                                    ))}
                                  </ul>
                                  {/* <button type="button" className="more">
                                  показать все
                                </button> */}
                                </Accordion.Body>
                              </Accordion.Item>
                            ) : null;
                          })}
                      </Accordion>
                      <Button
                        variant="primary"
                        className="w-100 rounded-3"
                        onClick={() => onLoad()}
                      >
                        Применить
                      </Button>
                    </form>
                  </Offcanvas.Body>
                </Offcanvas>
              </div>
            </Col>
            <Col lg={9}>
              <div className="d-lg-flex justify-content-between mb-5">
                <div>
                  {category?.item?.child?.length > 0 && (
                    <div className="subcategories-slider">
                      {category.item.child
                        .filter((e) => e.productCount > 0)
                        .map((e) => (
                          <NavLink to={"/category/" + e.id}>{e.title}</NavLink>
                        ))}
                    </div>
                  )}
                </div>
                <div>
                  <Select
                    className="flex-1"
                    title="Сортировать по"
                    onClick={(e) => setValue("sort", e.value)}
                    value={data?.sort ? data.sort : ""}
                    data={[
                      { title: "Все", value: "" },
                      // { title: "Новое", value: "new" },
                      // { title: "Старое", value: "old" },
                      { title: "Дешевле", value: "cheaper" },
                      { title: "Дороже", value: "expensive" },
                    ]}
                  />
                  <button
                    type="button"
                    onClick={handleShow}
                    className="input d-lg-none p-2 mt-lg-0 mt-3 w-fit"
                  >
                    <Filter className="fs-14 dark-gray" />
                  </button>
                </div>
              </div>
              <Row
                xs={2}
                sm={3}
                xxl={4}
                className="gx-4 gy-5 justify-content-center"
              >
                <DataTable
                  data={category.item?.products?.items}
                  pagination={category.item?.products?.pagination}
                  renderItem={(e) =>
                    !e.type || e.type == "dish" || e.type == "product" ? (
                      <Col>
                        <ProductCard
                          key={e.id}
                          data={e}
                          onLoad={(e) =>
                            setProduct((prev) => ({
                              ...prev,
                              show: true,
                              loading: false,
                              data: e,
                            }))
                          }
                          onFeedback={setShowFeedback}
                        />
                      </Col>
                    ) : e.type == "project" ? (
                      <Col>
                        <ProjectItem data={e} />
                      </Col>
                    ) : (
                      <Col>
                        <ServiceItem data={e} />
                      </Col>
                    )
                  }
                />
              </Row>
            </Col>
          </Row>
          <Callback
            show={!!showFeedback}
            product={showFeedback}
            setShow={setShowFeedback}
          />
        </Container>
      </section>
    </main>
  );
};

export default Category;
